import { useEffect, useState } from 'react';

import { api } from '../services/api';
import type {
  ColumnInference,
  CrossFieldFlag,
  HitSeverity,
  ReviewerDefinedRuleResponse,
  RunStatus,
} from '../services/types';

import { AddRuleModal } from './AddRuleModal';
import { Button } from './Button';
import { Spinner } from './Spinner';

export interface CrossFieldFlagsTableProps {
  runId: string;
  /** Page size. Default 100 to match the backend's default; max 500. */
  pageSize?: number;
  /**
   * v0.5 Stage E.3 — Run status. The "Add rule" button is gated on
   * ``"complete"`` / ``"awaiting_review"`` so the reviewer can only
   * author rules once the pipeline has settled. ``undefined`` /
   * ``null`` hides the button (legacy callers, mid-run renders).
   */
  runStatus?: RunStatus | null;
  /**
   * v0.5 Stage E.3 — Run's column inference list. Drives the
   * AddRuleModal's column dropdowns + operator-by-type filtering.
   * When omitted the "Add rule" button is hidden — without column
   * metadata the modal can't render its dropdowns.
   */
  columns?: ColumnInference[];
  /**
   * v0.5 Stage E.3 — reviewer identity passed through to
   * ``createReviewerDefinedRule``. Required when the Add rule
   * button is shown; undefined hides the button.
   */
  reviewerId?: string;
  /**
   * v0.5 Stage E.3 — fired after the backend accepts a reviewer-
   * defined rule (and re-kicks the run). The parent typically
   * refreshes the run summary so the reviewer sees the new
   * violation list once the validator finishes.
   */
  onRuleCreated?: (response: ReviewerDefinedRuleResponse) => void;
}

const severityStyles: Record<HitSeverity, string> = {
  low: 'bg-surface-alt text-muted',
  medium: 'bg-warn/15 text-warn',
  high: 'bg-danger/15 text-danger',
};

/**
 * Paginated table of cross-field violation flags for a run.
 *
 * Reads from `GET /runs/:run_id/cross_field_flags` (Stage D.4). Paginates
 * with a "Load more" button at the bottom — same pattern the existing
 * Anomalies tab uses, so reviewers don't learn a new control.
 *
 * Filters out per-rule truncation summary rows (`row_id === -1`) so the
 * table only shows actionable rows. The total count includes summaries
 * for transparency; the rendered count subtracts them.
 *
 * Empty state: an empty page with a positive message ("0 violations")
 * when the backend reports `total === 0`. Error state: an inline
 * message + a Retry button.
 */
export function CrossFieldFlagsTable({
  runId,
  pageSize = 100,
  runStatus,
  columns,
  reviewerId,
  onRuleCreated,
}: CrossFieldFlagsTableProps) {
  const [flags, setFlags] = useState<CrossFieldFlag[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [addRuleOpen, setAddRuleOpen] = useState(false);

  // v0.5 Stage E.3 — show the Add rule button only when:
  //   * the run has settled (status: complete or awaiting_review)
  //   * column metadata is available (drives the modal's dropdowns)
  //   * reviewer_id is known (the audit row needs it)
  const canAddRule =
    !!reviewerId &&
    !!columns &&
    columns.length > 0 &&
    (runStatus === 'complete' || runStatus === 'awaiting_review');

  // First page on mount + whenever runId changes.
  useEffect(() => {
    setFlags([]);
    setTotal(null);
    setError(null);
    setOffset(0);
    void loadPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  async function loadPage(nextOffset: number, replace: boolean): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCrossFieldFlags(runId, {
        limit: pageSize,
        offset: nextOffset,
      });
      setTotal(res.total);
      setOffset(nextOffset + res.row_flags.length);
      setFlags((prev) => (replace ? res.row_flags : prev.concat(res.row_flags)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  }

  // Truncation summary rows have row_id = -1; backend uses them to
  // signal that violations were capped. We hide them from the table
  // but keep them in the total for the "loaded N of M" footer line.
  const visibleFlags = flags.filter((f) => f.row_id !== -1);
  const truncationCount = flags.length - visibleFlags.length;
  const hasMore = total !== null && offset < total;

  if (loading && flags.length === 0) {
    return (
      <div className="py-6 text-center">
        <Spinner /> Loading violations…
      </div>
    );
  }

  if (error && flags.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-danger">{error}</p>
        <Button variant="secondary" onClick={() => void loadPage(0, true)}>
          Retry
        </Button>
      </div>
    );
  }

  if (total === 0) {
    return (
      <section
        aria-labelledby="cross-field-flags-title"
        className="space-y-3"
        data-testid="cross-field-flags-table"
      >
        <header className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3
            id="cross-field-flags-title"
            className="m-0 text-sm font-semibold"
          >
            Cross-field violations
          </h3>
          {canAddRule && (
            <Button
              variant="secondary"
              onClick={() => setAddRuleOpen(true)}
              data-testid="cross-field-add-rule"
            >
              + Add rule
            </Button>
          )}
        </header>
        <p className="text-sm text-muted">
          No cross-field violations on this run. Confirmed rules are in
          force; the cleansed output passed every check.
        </p>
        {canAddRule && columns && reviewerId && (
          <AddRuleModal
            runId={runId}
            columns={columns}
            isOpen={addRuleOpen}
            onClose={() => setAddRuleOpen(false)}
            onCreated={onRuleCreated}
            reviewerId={reviewerId}
          />
        )}
      </section>
    );
  }

  return (
    <section
      aria-labelledby="cross-field-flags-title"
      className="space-y-3"
      data-testid="cross-field-flags-table"
    >
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 id="cross-field-flags-title" className="m-0 text-sm font-semibold">
          Cross-field violations
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {total !== null && (
            <span className="text-xs text-muted font-mono">
              {visibleFlags.length} shown · {total} total
              {truncationCount > 0 && ` · ${truncationCount} truncated`}
            </span>
          )}
          {canAddRule && (
            <Button
              variant="secondary"
              onClick={() => setAddRuleOpen(true)}
              data-testid="cross-field-add-rule"
            >
              + Add rule
            </Button>
          )}
        </div>
      </header>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm" role="table">
          <thead className="bg-surface-alt/50 text-xs uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="text-left px-3 py-2">row</th>
              <th scope="col" className="text-left px-3 py-2">columns</th>
              <th scope="col" className="text-left px-3 py-2">values</th>
              <th scope="col" className="text-left px-3 py-2">issue</th>
              <th scope="col" className="text-left px-3 py-2">severity</th>
            </tr>
          </thead>
          <tbody>
            {visibleFlags.map((f, i) => (
              <tr
                key={`${f.row_id}-${f.rule_id}-${i}`}
                className="border-t border-border/50"
                data-testid="cross-field-flag-row"
              >
                <td className="px-3 py-2 font-mono text-xs">{f.row_id}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {f.col_a}
                  {f.col_b ? ` ↔ ${f.col_b}` : ''}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  <span className="block">{formatCell(f.col_a_value)}</span>
                  {f.col_b !== null && (
                    <span className="block">{formatCell(f.col_b_value)}</span>
                  )}
                </td>
                <td className="px-3 py-2">{f.issue}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded-sm px-1.5 py-0.5 text-xs font-semibold ${
                      severityStyles[f.severity]
                    }`}
                  >
                    {f.severity}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex items-center justify-between gap-3">
          {error && <p className="text-xs text-danger m-0">{error}</p>}
          <Button
            variant="secondary"
            onClick={() => void loadPage(offset, false)}
            disabled={loading}
            data-testid="cross-field-flags-load-more"
            className="ml-auto"
          >
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
      {canAddRule && columns && reviewerId && (
        <AddRuleModal
          runId={runId}
          columns={columns}
          isOpen={addRuleOpen}
          onClose={() => setAddRuleOpen(false)}
          onCreated={onRuleCreated}
          reviewerId={reviewerId}
        />
      )}
    </section>
  );
}

function formatCell(v: string | null): React.ReactNode {
  if (v === null) return <span className="italic text-muted">(null)</span>;
  if (v === '') return <span className="italic text-muted">(empty)</span>;
  return v;
}
